// React-Query hooks для Sprint 5A.2 Biomarker Insights (Stage 1 §7 + §4.3 контракты).
//
// GET /api/v1/insights/biomarkers/summary — Stage 3 §10.1 GetBiomarkerSummaryController.
// GET /api/v1/insights/biomarkers/{code}  — Stage 3 §10.2 GetBiomarkerInsightController.
//
// Status-based response. Polling каждые 5 сек когда status='generating' AND enabled=true
// AND query NOT in error state; останавливается на 'ready' OR terminal status (disabled,
// no_uploads, no_actionable_biomarkers, no_verified_rule, no_range) OR enabled=false
// OR query.state.status==='error' (D4-S5A2-3).
// staleTime 5 минут — cache hit для повторных открытий главной без regen.
//
// 60-сек timeout — реализован в BiomarkerSummaryCard / BiomarkerInsightBlock: component
// owns isTimedOut state, передаёт `enabled: !isTimedOut` сюда → react-query прекращает
// refetchInterval cycles.
//
// Retry — наследует global baseline (retry:1 из main.tsx); biomarker не override'ит
// (D4-S5A2-6 — re-use 5A.1 D4-S5A-6 rationale).
//
// `query.state.status === 'error'` gate в refetchInterval (D4-S5A2-3) — production-validated
// fix из wellness.ts:23-35 (Sayat R1 M-H2 v0.99.106+ post-5A.1-Stage-4-sign-off).

import { useQuery, type UseQueryOptions, type UseQueryResult } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { api } from '@/api/client';
import { queryKeys } from '@/api/queryKeys';
import type { ApiError, BiomarkerInsightResponse, BiomarkerSummaryResponse } from '@/api/types';

// ----- /summary hook -----

export type UseBiomarkerSummaryOptions = {
  enabled?: boolean;
};

export function biomarkerSummaryRefetchInterval(
  query: {
    state: {
      data: BiomarkerSummaryResponse | undefined;
      status?: 'pending' | 'error' | 'success';
    };
  },
): number | false {
  // D4-S5A2-3 — error state gate (production lesson из wellness.ts:31).
  if (query.state.status === 'error') return false;
  const data = query.state.data;
  if (data?.status === 'generating') return 5_000;
  return false;
}

export function biomarkerSummaryQueryOptions(
  options: UseBiomarkerSummaryOptions = {},
): UseQueryOptions<BiomarkerSummaryResponse, AxiosError<ApiError>> {
  const { enabled = true } = options;
  return {
    queryKey: queryKeys.biomarkerSummary,
    queryFn: async () => {
      const { data } = await api.get<BiomarkerSummaryResponse>('/insights/biomarkers/summary');
      return data;
    },
    enabled,
    refetchInterval: biomarkerSummaryRefetchInterval,
    refetchIntervalInBackground: false,
    staleTime: 5 * 60 * 1000,
  };
}

export function useBiomarkerSummary(
  options: UseBiomarkerSummaryOptions = {},
): UseQueryResult<BiomarkerSummaryResponse, AxiosError<ApiError>> {
  return useQuery(biomarkerSummaryQueryOptions(options));
}

// ----- /{code} hook -----

export type UseBiomarkerInsightOptions = {
  enabled?: boolean;
};

export function biomarkerInsightRefetchInterval(
  query: {
    state: {
      data: BiomarkerInsightResponse | undefined;
      status?: 'pending' | 'error' | 'success';
    };
  },
): number | false {
  if (query.state.status === 'error') return false;
  const data = query.state.data;
  if (data?.status === 'generating') return 5_000;
  return false;
}

export function biomarkerInsightQueryOptions(
  code: string,
  options: UseBiomarkerInsightOptions = {},
): UseQueryOptions<BiomarkerInsightResponse, AxiosError<ApiError>> {
  const { enabled = true } = options;
  return {
    queryKey: queryKeys.biomarkerInsight(code),
    queryFn: async () => {
      const { data } = await api.get<BiomarkerInsightResponse>(`/insights/biomarkers/${code}`);
      return data;
    },
    enabled,
    refetchInterval: biomarkerInsightRefetchInterval,
    refetchIntervalInBackground: false,
    staleTime: 5 * 60 * 1000,
  };
}

export function useBiomarkerInsight(
  code: string,
  options: UseBiomarkerInsightOptions = {},
): UseQueryResult<BiomarkerInsightResponse, AxiosError<ApiError>> {
  return useQuery(biomarkerInsightQueryOptions(code, options));
}
